import type { IzQuestion } from '../../question-bank/izteach';
import { bareIzteachClient } from '../clients';
import { retryColdStart } from '../core/coldStart';
import { izteachSession, libraryVendorId } from '../session';

const LIMIT = 500;
/** Ids per id_in request, as in the question bank. */
const CHILD_CHUNK = 40;

function rows(data: unknown): IzQuestion[] {
  return Array.isArray(data) ? (data as IzQuestion[]) : [];
}

// examination-service /question: the public GET strips `correct`; /question/admin keeps it for a teacher token.
// Bare client on purpose: a denied answer key must never sign the teacher out of IZ.
export const ExamQuestionRepository = {
  publicByExam: (examId: string) =>
    retryColdStart(() =>
      bareIzteachClient('exam')
        .get('/question', { params: { vendorId: libraryVendorId(), exam: examId, _limit: LIMIT } })
        .then((res) => rows(res.data)),
    ),

  /** Null when there is no IZ session or the gateway refuses it. */
  keyedByExam: (examId: string): Promise<IzQuestion[] | null> => {
    if (!izteachSession.accessToken) return Promise.resolve(null);
    return bareIzteachClient('exam')
      .get('/question/admin', {
        params: { vendorId: libraryVendorId(), exam: examId, _limit: LIMIT },
        headers: { Authorization: izteachSession.accessToken },
      })
      .then((res) => (Array.isArray(res.data) ? (res.data as IzQuestion[]) : null), () => null);
  },

  /** The exam document (its type and draw matrix); null without a session or on refusal. */
  examById: (examId: string): Promise<unknown> => {
    if (!izteachSession.accessToken) return Promise.resolve(null);
    return bareIzteachClient('exam')
      .get(`/exam/${examId}`, { params: { vendorId: libraryVendorId() }, headers: { Authorization: izteachSession.accessToken } })
      .then((res) => res.data as unknown, () => null);
  },

  /**
   * Group children by id; null on failure. A copied course keeps its groups pointing at the source
   * tenant's children, so the keyed read names no vendor (the ids scope it); else one public GET per child.
   */
  childrenByIds: (ids: string[], keyed: boolean): Promise<IzQuestion[] | null> => {
    if (ids.length === 0) return Promise.resolve([]);
    const publicEach = () => Promise.all(ids.map((id) => bareIzteachClient('exam')
      .get(`/question/${id}`, { params: { vendorId: libraryVendorId() } })
      .then((res) => res.data as IzQuestion)))
      .then((list) => list.filter(Boolean), () => null);
    const token = izteachSession.accessToken;
    if (!keyed || !token) return publicEach();
    const chunks: string[][] = [];
    for (let i = 0; i < ids.length; i += CHILD_CHUNK) chunks.push(ids.slice(i, i + CHILD_CHUNK));
    return Promise.all(chunks.map((chunk) => bareIzteachClient('exam')
      .get('/question/admin', { params: { id_in: chunk, _limit: chunk.length }, headers: { Authorization: token } })
      .then((res) => rows(res.data))))
      .then((lists) => lists.reduce<IzQuestion[]>((all, list) => all.concat(list), []), () => null)
      .then((found) => (found && found.length > 0 ? found : publicEach()));
  },
};

export default ExamQuestionRepository;
